import React, { useState, useEffect, useCallback } from 'react';
import { Users, UserPlus, Footprints, Ticket } from 'lucide-react';
import RecentActivity from './RecentActivity';
import MetricCard from './MetricCard';
import { apiFetch } from '../../utils/apiClient';

const PERIOD_OPTIONS = [
  { value: 'today', label: '오늘' },
  { value: '1m', label: '1개월' },
  { value: '3m', label: '3개월' },
  { value: '6m', label: '6개월' },
  { value: '12m', label: '12개월' },
];

const Dashboard = ({ storeInfo }) => {
  const [period, setPeriod] = useState('today');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchStats = useCallback(async () => {
    if (!storeInfo?.id) return;
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(
        `/api/dashboard/stats?store_id=${encodeURIComponent(storeInfo.id)}&period=${period}`
      );
      const result = await response.json();
      if (result.success) {
        setStats(result.data);
      } else {
        setError(result.message || '통계를 불러오지 못했습니다.');
      }
    } catch (err) {
      setError('네트워크 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [storeInfo, period]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const periodLabel = PERIOD_OPTIONS.find((opt) => opt.value === period)?.label || '';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-bold text-gray-900">대시보드</h2>
        <div className="flex rounded-full border border-gray-200 bg-white p-1 shadow-sm">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-3.5 py-1.5 text-sm font-medium rounded-full transition-colors ${
                period === opt.value
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 text-sm rounded-xl p-3">{error}</div>
      )}

      {loading && !stats ? (
        <div className="text-center py-12 text-gray-500 text-sm">불러오는 중...</div>
      ) : stats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard icon={Users} label="총 고객 수" value={`${stats.total_customers}명`} color="indigo" />
          <MetricCard icon={UserPlus} label={`${periodLabel} 신규 고객`} value={`${stats.period_new_customers}명`} color="emerald" />
          <MetricCard icon={Footprints} label={`${periodLabel} 방문자`} value={`${stats.period_visitors}명`} color="amber" />
          <MetricCard
            icon={Ticket}
            label={`${periodLabel} 쿠폰 사용`}
            value={`${stats.period_coupons_used}개`}
            sub={stats.period_coupons_issued > 0 ? `발급 ${stats.period_coupons_issued}개` : '발급된 쿠폰 없음'}
            color="rose"
          />
        </div>
      ) : null}

      {/* 최근 방문 */}
      <RecentActivity storeInfo={storeInfo} />
    </div>
  );
};

export default Dashboard;
